import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { buildApiUrl } from '../config/constants';

interface Category {
  category: string;
  count: number;
}

const fallbackCategories: Category[] = [
  { category: 'Ficțiune', count: 0 },
  { category: 'Clasic', count: 0 },
  { category: 'Poezie', count: 0 },
  { category: 'Istorie', count: 0 }
];

const categoryIcon = (name: string) => {
  const normalized = name.toLowerCase();

  if (normalized.includes('fic')) return '📚';
  if (normalized.includes('poe')) return '🖋️';
  if (normalized.includes('istor')) return '🏛️';
  if (normalized.includes('clasic')) return '📜';
  if (normalized.includes('cop')) return '🧒';
  if (normalized.includes('fant')) return '🐉';
  if (normalized.includes('știin') || normalized.includes('stiin')) return '🔬';
  if (normalized.includes('mister')) return '🕵️';
  return '📘';
};

const categoryDescription = (name: string) => {
  const normalized = name.toLowerCase();

  if (normalized.includes('clasic')) return 'Autori canonici și texte fundamentale';
  if (normalized.includes('fic')) return 'Romane, povestiri și literatură imaginativă';
  if (normalized.includes('poe')) return 'Versuri, antologii și lirică';
  if (normalized.includes('istor')) return 'Evenimente, documente și interpretări istorice';
  if (normalized.includes('cop')) return 'Lecturi pentru copii și adolescenți';
  if (normalized.includes('fant')) return 'Universuri speculative și aventuri';
  if (normalized.includes('știin') || normalized.includes('stiin')) return 'Știință, cunoaștere și popularizare';
  return 'Resurse disponibile în biblioteca digitală';
};

const CategoriesPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState<Category[]>(fallbackCategories);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(buildApiUrl('/books/categories'));
        if (!response.ok) {
          throw new Error('Nu s-au putut încărca categoriile');
        }

        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          setCategories(data);
        }
      } catch (error) {
        console.error('Eroare la încărcarea categoriilor:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const filteredCategories = useMemo(
    () =>
      categories.filter((category) =>
        category.category.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [categories, searchTerm]
  );

  const totalBooks = useMemo(
    () => categories.reduce((sum, category) => sum + category.count, 0),
    [categories]
  );

  const popularCategories = useMemo(
    () => [...categories].sort((a, b) => b.count - a.count).slice(0, 3),
    [categories]
  );

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Header />
      <div className="pt-24 md:pt-28"></div>

      <main className="flex-grow">
        <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Categorii de Cărți</h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Explorează categoriile reale din biblioteca locală și intră direct în colecția filtrată.
              </p>
            </div>
          </div>
        </section>

        <section className="py-8">
          <div className="container mx-auto px-4">
            <div className="max-w-md mx-auto">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Caută după categorie..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <svg
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 bg-blue-50">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">{totalBooks.toLocaleString()}</div>
                <div className="text-gray-600">Total Cărți Publice</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">{categories.length}</div>
                <div className="text-gray-600">Categorii Active</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">{loading ? '...' : 'Live'}</div>
                <div className="text-gray-600">Date din Biblioteca Locală</div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            {filteredCategories.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-gray-400 text-6xl mb-4">🔍</div>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">
                  Nu am găsit categorii care să corespundă căutării
                </h3>
                <p className="text-gray-500">Încearcă cu un termen diferit</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredCategories.map((category, index) => (
                  <Link
                    key={`${category.category}-${index}`}
                    to={`/books?category=${encodeURIComponent(category.category)}`}
                    className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all transform hover:-translate-y-1 group"
                  >
                    <div className="text-center">
                      <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">
                        {categoryIcon(category.category)}
                      </div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors">
                        {category.category}
                      </h3>
                      <p className="text-sm text-gray-600 mb-3">
                        {categoryDescription(category.category)}
                      </p>
                      <div className="flex items-center justify-center text-blue-600 font-medium">
                        <span className="mr-1">{category.count}</span>
                        <span className="text-sm">cărți</span>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        {popularCategories.length > 0 && (
          <section className="py-16 bg-gray-100">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">Cele Mai Populare Categorii</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {popularCategories.map((category, index) => (
                  <Link
                    key={`${category.category}-${index}`}
                    to={`/books?category=${encodeURIComponent(category.category)}`}
                    className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-all group"
                  >
                    <div className="text-center">
                      <div className="text-6xl mb-4 group-hover:scale-110 transition-transform">
                        {categoryIcon(category.category)}
                      </div>
                      <h3 className="text-2xl font-bold text-gray-800 mb-3 group-hover:text-blue-600 transition-colors">
                        {category.category}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {categoryDescription(category.category)}
                      </p>
                      <div className="text-3xl font-bold text-blue-600 mb-2">{category.count}</div>
                      <div className="text-sm text-gray-500">cărți disponibile</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default CategoriesPage;
